import {useCallback} from 'react'
import {
    useQueryUserPreference,
    useQueryAllUserPreferences,
    useUpsertUserPreferenceMutation,
    useDeleteUserPreferenceMutation,
    useQueryCurrentUser,
} from '../../query/user-preferences-management/user-preferences-management'
import {type User} from '../../api/basal-ganglia/users/types/users.types'
import {type UserPreference} from '../../api/basal-ganglia/user-preferences-management/types/user-preferences-management.types'

interface UseUserPreferencesManagementReturn<T> {
    preference: UserPreference<T> | null | undefined
    allPreferences: UserPreference[] | undefined
    currentUser: User | undefined
    preferenceValue: T
    isLoadingPreference: boolean
    isLoadingAllPreferences: boolean
    isLoadingCurrentUser: boolean
    error: Error | undefined
    isSaving: boolean
    isDeleting: boolean
    getPreferenceValue: (preferenceKey?: string) => T | undefined
    hasPreference: (preferenceKey?: string) => boolean
    savePreference: (preferenceKey: string, value: T) => void
    deletePreference: (preferenceKey: string) => void
    saveCurrentPreference: (value: T) => void
    deleteCurrentPreference: () => void
}

export function useUserPreferencesManagement<T = unknown>(
    key?: string,
    options?: {fetchAll?: boolean; fetchCurrentUser?: boolean},
): UseUserPreferencesManagementReturn<T> {
    const {
        data: preference,
        isLoading: isLoadingPreference,
        error: preferenceError,
    } = useQueryUserPreference<T>(key || '', {
        enabled: !!key,
    })

    const {
        data: allPreferences,
        isLoading: isLoadingAllPreferences,
        error: allPreferencesError,
    } = useQueryAllUserPreferences({
        enabled: options?.fetchAll === true,
    })

    const {
        data: currentUser,
        isLoading: isLoadingCurrentUser,
        error: currentUserError,
    } = useQueryCurrentUser({
        enabled: options?.fetchCurrentUser === true,
    })

    const upsertMutation = useUpsertUserPreferenceMutation<T>()
    const deleteMutation = useDeleteUserPreferenceMutation()

    const getPreferenceValue = useCallback(
        (preferenceKey?: string): T | undefined => {
            if (preferenceKey) {
                if (preferenceKey === key) {
                    return preference?.value.value as T
                }
                return allPreferences?.find((p) => p.key === preferenceKey)?.value.value as T
            }
            return preference?.value.value as T
        },
        [preference, allPreferences, key],
    )

    const hasPreference = useCallback(
        (preferenceKey?: string): boolean => {
            const targetKey = preferenceKey || key
            if (!targetKey) {
                return false
            }

            if (targetKey === key) {
                return !!preference
            }
            return allPreferences?.some((p) => p.key === targetKey) ?? false
        },
        [preference, allPreferences, key],
    )

    const savePreference = useCallback(
        (preferenceKey: string, value: T) => {
            upsertMutation.mutate({
                key: preferenceKey,
                value,
            })
        },
        [upsertMutation],
    )

    const deletePreference = useCallback(
        (preferenceKey: string) => {
            deleteMutation.mutate({
                key: preferenceKey,
            })
        },
        [deleteMutation],
    )

    const saveCurrentPreference = useCallback(
        (value: T) => {
            if (!key) {
                throw new Error('Cannot save current preference: no key specified')
            }
            savePreference(key, value)
        },
        [key, savePreference],
    )

    const deleteCurrentPreference = useCallback(() => {
        if (!key) {
            throw new Error('Cannot delete current preference: no key specified')
        }
        deletePreference(key)
    }, [key, deletePreference])

    const error = preferenceError || allPreferencesError || currentUserError || undefined

    return {
        preference,
        allPreferences,
        currentUser,
        preferenceValue: preference?.value.value as T,
        isLoadingPreference,
        isLoadingAllPreferences,
        isLoadingCurrentUser,
        error,
        isSaving: upsertMutation.isPending,
        isDeleting: deleteMutation.isPending,

        getPreferenceValue,
        hasPreference,

        savePreference,
        deletePreference,

        saveCurrentPreference,
        deleteCurrentPreference,
    }
}
